import { Injectable } from '@angular/core';
import {
  GetSupplierBlendInterface,
  SupplierResponseInterface,
  SurveyRequest,
} from '../../../shared/interfaces/survey.interface';
import { Qualification } from '../../../shared/interfaces/qualification.interface';
import { FlexibilityPricesParams } from '../../../shared/interfaces/supplier-api.interface';
import {
  FeasibilityExclusionData,
  FeasibilityRequest,
  FeasibilityResponse,
} from '../../../shared/interfaces/feasibility.interface';
import { FeasibilityService } from './feasibility.service';
import { map, switchMap, toArray } from 'rxjs/operators';
import { PriceMultiplier } from '../../layout/header/feasibility/domain/price-multiplier';
import { Supplier } from '../../shared/interfaces/pricing.interface';
import {
  GetBuyerData,
  SurveyTopBarData,
} from '../../../shared/interfaces/survey-meta-data.interface';
import { Observable } from 'rxjs';
import { SupplierApiService } from '../../../shared/services/supplier-api/supplier-api.service';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { PricingService } from '../pricing/pricing.service';
import { FeasibilityDataMapper } from '../domain/feasibility-data-mapper';
import { FeasibilityTotal } from '../../layout/header/feasibility/feasibility.class';
import { CpiService } from '../cpi/cpi.service';
import { PurePriceOverride } from '../domain/pure-price-override';

@Injectable({
  providedIn: 'root',
})
export class FeasibilityDataV2Service {
  constructor(
    private _feasibilityService: FeasibilityService,
    private _buyerApiService: BuyerApiService,
    private _supplierApiService: SupplierApiService,
    private _pricingService: PricingService,
    private _cpiService: CpiService
  ) {}

  public getCpiFeasibilityData(
    surveyId: number,
    language: string,
    surveyRequest: SurveyRequest,
    qualifications: Qualification[],
    suppliers: SupplierResponseInterface[],
    flexibilityPriceParams: FlexibilityPricesParams,
    supplierBlends: GetSupplierBlendInterface[],
    eligibleSuppliers: Supplier[],
    feasibilityExclusion?: FeasibilityExclusionData,
    isMcSurvey = false,
    defaultPurePriceOverride = new PurePriceOverride(false, 0)
  ) {
    const feasibilityPayload = this.getFeasibilityRequestPayload(
      surveyId,
      surveyRequest,
      qualifications,
      suppliers,
      feasibilityExclusion
    );

    return this._feasibilityService
      .getFeasibility(feasibilityPayload.request)
      .pipe(
        map((feasibilityResponse) => {
          if (defaultPurePriceOverride.isActive()) {
            flexibilityPriceParams.survey_price_override =
              defaultPurePriceOverride.getPrice();
          }
          this.addPriceMultiplierInSupplierData(
            suppliers,
            feasibilityResponse.pricing_multplier,
            flexibilityPriceParams.manual_override,
            flexibilityPriceParams.survey_price_override
          );

          // adding feasibility count for each supplier
          let mappedSuppliers = this.addFeasibilityCountInSupplierData(
            suppliers,
            feasibilityResponse
          );

          // Mapping suppliers to include default blend data or supplier group data
          mappedSuppliers = this.mapGroupSupplierCpis(
            suppliers,
            flexibilityPriceParams,
            eligibleSuppliers
          );

          const mapper = new FeasibilityDataMapper(
            surveyId,
            language,
            surveyRequest,
            feasibilityResponse
          );

          const feasibility = isMcSurvey
            ? new FeasibilityTotal(
                feasibilityResponse,
                feasibilityPayload.quotaSet
              ).totalFeasibility()
            : 0;
          const cpi = isMcSurvey
            ? this._calculateTotalCpi(
                feasibilityResponse,
                mappedSuppliers,
                mapper.buyerInfo(),
                flexibilityPriceParams
              )
            : 0;

          return {
            mapper,
            mappedSuppliers,
            feasibility,
            cpi,
          };
        }),
        map(({ mapper, mappedSuppliers, feasibility, cpi }) => {
          return {
            supplierSpecificInfo: mapper.suppliers(
              supplierBlends,
              eligibleSuppliers
            ),
            cpiData: mapper.cpi(mappedSuppliers, flexibilityPriceParams),
            feasibilityData: mapper.feasibility(feasibilityPayload),
            ...(isMcSurvey && {
              mcData: mapper.mc(cpi, feasibility, flexibilityPriceParams),
            }),
          };
        })
      );
  }

  public getFlexibilityPriceParams(
    surveyId: number
  ): Observable<FlexibilityPricesParams> {
    return this._supplierApiService.getFlexibilityPricesParams(surveyId);
  }

  public getSuppliers(
    surveyId: number,
    language: string,
    surveyRequest: SurveyRequest
  ): Observable<SupplierResponseInterface[]> {
    const { expected_loi, expected_ir, field_time, completes_required } =
      surveyRequest;
    return this._buyerApiService
      .getSuppliers(
        surveyId,
        'editable',
        expected_loi,
        expected_ir,
        language,
        field_time,
        completes_required
      )
      .pipe(
        switchMap((suppliers = []) => suppliers),
        map((supplier) => ({
          ...supplier,
          completes_goal:
            surveyRequest.estimated_clicks || supplier.completes_goal,
          isChecked: supplier.is_active === true && supplier.quantity > 0,
        })),
        toArray()
      );
  }

  public getFeasibilityRequestPayload(
    surveyId: number,
    surveyRequest: SurveyRequest,
    qualifications: Qualification[],
    suppliers: SupplierResponseInterface[],
    feasibilityExclusion?: FeasibilityExclusionData
  ) {
    const supplier_ids = this._feasibilityService.mapSupplierIds(suppliers);
    return this._feasibilityService.buildFeasibilityPayload(
      qualifications,
      surveyRequest,
      surveyId,
      supplier_ids,
      feasibilityExclusion?.survey_grouping,
      feasibilityExclusion?.is_exclusion
    );
  }

  public getFeasibilityData(
    feasibilityRequest: FeasibilityRequest
  ): Observable<FeasibilityResponse> {
    return this._feasibilityService.getFeasibility(feasibilityRequest);
  }

  public getEligibleSuppliers(surveyTopBarData: SurveyTopBarData) {
    const {
      location,
      expected_ir,
      incl_excl,
      expected_loi,
      is_pure_price_rate_card_enable,
      surveyFieldTime,
    } = surveyTopBarData;
    return this._pricingService.getEligibleSuppliers(
      location!.countryCode,
      location!.languageShort,
      expected_loi.toString(),
      expected_ir.toString(),
      incl_excl.toString(),
      is_pure_price_rate_card_enable,
      surveyFieldTime.toString()
    );
  }

  // Helpers
  public addPriceMultiplierInSupplierData(
    suppliers: SupplierResponseInterface[],
    pricingMultiplier: number,
    manualOperatorOverride: boolean,
    surveyPriceOverride: number = 0
  ) {
    const priceMultiplier = new PriceMultiplier(suppliers);
    priceMultiplier.calculate(
      pricingMultiplier,
      manualOperatorOverride,
      surveyPriceOverride
    );
  }

  public addFeasibilityCountInSupplierData(
    suppliers: SupplierResponseInterface[],
    feasibilityResponse: FeasibilityResponse
  ) {
    return suppliers.map((supplier) => {
      supplier.suppliers.forEach((singleSup) => {
        singleSup.feasibility = this._getSupplierFeasibilityCount(
          singleSup.id,
          feasibilityResponse
        );
      });
      return supplier;
    });
  }

  private _getSupplierFeasibilityCount(
    supplierId: number,
    response: FeasibilityResponse
  ) {
    const supplierWithFeasibility = response.suppliers.find(
      (sup) => Number(sup.supplier_id) === Number(supplierId)
    );
    return supplierWithFeasibility ? supplierWithFeasibility.count : 0;
  }

  public mapGroupSupplierCpis(
    suppliers: SupplierResponseInterface[],
    flexibilityParamsResponse: FlexibilityPricesParams,
    eligibleSuppliers: Supplier[]
  ): SupplierResponseInterface[] {
    for (const supplier of suppliers) {
      if (supplier.group_ref) {
        if (
          flexibilityParamsResponse.survey_price_override ||
          flexibilityParamsResponse.manual_override
        ) {
          continue;
        }
        supplier.cpi = this._calculateGroupedSupplierCpi(
          supplier,
          eligibleSuppliers
        );
      }
    }
    return suppliers;
  }

  private _calculateGroupedSupplierCpi(
    supplier: SupplierResponseInterface,
    eligibleSuppliers: Supplier[] = []
  ) {
    let cpiAmount = 0;
    for (const groupedSupplier of supplier.suppliers) {
      const eligibleSupplierData = eligibleSuppliers.filter(
        (eligibleSupplier) => eligibleSupplier.id === groupedSupplier.id
      );
      if (eligibleSupplierData.length) {
        cpiAmount += eligibleSupplierData[0].cpi;
      }
    }
    return Number((cpiAmount / supplier.suppliers.length).toFixed(2));
  }

  private _calculateTotalCpi(
    response: FeasibilityResponse,
    suppliersRequest: SupplierResponseInterface[],
    buyerData: GetBuyerData,
    flexbilityPriceParams: FlexibilityPricesParams,
    isActiveSurvey = true
  ) {
    if (flexbilityPriceParams.survey_price_override) {
      return flexbilityPriceParams.survey_price_override;
    }

    const mappedCpis = this._cpiService.mapCpi(
      suppliersRequest,
      buyerData,
      response,
      isActiveSurvey
    );
    return this._cpiService.getTotalCpi(mappedCpis, response);
  }
}
